import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { catchError, of } from 'rxjs';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
} from '@/app/common/general-types/briefs.dto';
import { ApplicationStatusBadgeComponent } from '@/app/atoms/application-status-badge/application-status-badge.component';
import { CreatorApplicationDrawer } from '../application-drawer/application-drawer';

export type ActivityStatusFilter = 'all' | 'invited' | 'applied' | 'hired' | 'completed';

const STATUS_FILTERS: { key: ActivityStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'invited', label: 'Invited' },
  { key: 'applied', label: 'Applied' },
  { key: 'hired', label: 'Hired' },
  { key: 'completed', label: 'Completed' },
];

@Component({
  selector: 'ins-creator-activities',
  imports: [DatePipe, ApplicationStatusBadgeComponent, CreatorApplicationDrawer],
  templateUrl: './activities.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorActivities implements OnInit {
  @ViewChild('applicationDrawer') applicationDrawer?: CreatorApplicationDrawer;

  readonly statusFilters = STATUS_FILTERS;
  readonly selectedStatus = signal<ActivityStatusFilter>('all');
  readonly applications = signal<BriefApplicationResponseDto[] | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  private creatorUserId = signal<string | undefined>(undefined);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private briefApplicationService = inject(BriefApplicationService);

  ngOnInit(): void {
    const creatorId = this.route.parent?.snapshot.paramMap.get('creatorId') ?? undefined;
    this.creatorUserId.set(creatorId);
    if (creatorId) {
      this.loadApplications();
    }
  }

  selectStatus(status: ActivityStatusFilter): void {
    this.selectedStatus.set(status);
    this.loadApplications();
  }

  statusFilterClass(status: ActivityStatusFilter): string {
    return this.selectedStatus() === status
      ? 'bg-primary/10 text-accent ring-1 ring-primary/30'
      : 'text-secondary hover:bg-zinc-100 dark:hover:bg-zinc-800';
  }

  openApplication(application: BriefApplicationResponseDto): void {
    this.applicationDrawer?.open(application);
  }

  onApplicationUpdated(application: BriefApplicationResponseDto): void {
    this.applications.update((list) =>
      (list ?? []).map((item) => (item.id === application.id ? { ...item, ...application } : item)),
    );
  }

  briefTitle(application: BriefApplicationResponseDto): string {
    return application.brief?.title ?? 'Untitled brief';
  }

  briefSubtitle(application: BriefApplicationResponseDto): string {
    const campaign = application.brief?.campaign?.campaignName;
    const brand = application.brief?.businessAccount?.companyName;
    return [campaign, brand].filter(Boolean).join(' · ') || '—';
  }

  private loadApplications(): void {
    const creatorUserId = this.creatorUserId();
    if (!creatorUserId) {
      return;
    }

    this.loadingState.set('loading');
    this.briefApplicationService
      .filterCreatorsApplications(creatorUserId, this.selectedStatus())
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of([] as BriefApplicationResponseDto[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((apps) => {
        this.applications.set(apps);
        this.loadingState.set('loaded');
      });
  }

  protected readonly ApplicationStatusEnum = ApplicationStatusEnum;
}
